/**
 * GoalForm Component
 * Comprehensive form for creating/editing goals
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Target, Calendar, User } from 'lucide-react';
import { useGoal } from '@/features/goals/hooks/useGoals';
import { useCareTeam } from '@/features/care-team/hooks/useCareTeam';
import type {
  GoalFormData,
  CreateGoalRequest,
  GoalType,
  GoalMeasurementType,
  GoalPriority,
  ReviewFrequency,
  GOAL_TYPE_LABELS,
  MEASUREMENT_TYPE_LABELS,
  PRIORITY_LABELS,
  IEP_AREAS,
  GOAL_CATEGORIES,
} from '@/features/goals/types/goalsTypes';

// ============================================================================
// Types
// ============================================================================

interface GoalFormProps {
  childId: string;
  familyId: string;
  goalId?: string | null;
  onSubmit: (data: CreateGoalRequest) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const goalTypeOptions: { value: GoalType; label: string }[] = [
  { value: 'iep', label: 'IEP Goal' },
  { value: 'therapy', label: 'Therapy Goal' },
  { value: 'developmental', label: 'Developmental' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'academic', label: 'Academic' },
  { value: 'other', label: 'Other' },
];

const measurementTypeOptions: { value: GoalMeasurementType; label: string }[] = [
  { value: 'percentage', label: 'Percentage (0-100%)' },
  { value: 'count', label: 'Count/Frequency' },
  { value: 'rating', label: 'Rating Scale' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'custom', label: 'Custom Metric' },
];

const priorityOptions: { value: GoalPriority; label: string }[] = [
  { value: 'high', label: 'High Priority' },
  { value: 'medium', label: 'Medium Priority' },
  { value: 'low', label: 'Low Priority' },
];

const reviewFrequencyOptions: { value: ReviewFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const iepAreas = [
  'Communication',
  'Social/Emotional',
  'Motor Skills',
  'Cognitive',
  'Adaptive Behavior',
  'Academic - Reading',
  'Academic - Writing',
  'Academic - Math',
  'Transition',
  'Other',
];

const goalCategories = [
  'Motor - Gross Motor',
  'Motor - Fine Motor',
  'Communication - Expressive',
  'Communication - Receptive',
  'Social Skills',
  'Self-Care',
  'Cognitive',
  'Behavioral',
  'Academic',
  'Play Skills',
  'Sensory',
  'Feeding',
  'Other',
];

// ============================================================================
// Initial Form State
// ============================================================================

const initialFormData: GoalFormData = {
  title: '',
  description: '',
  goalType: 'therapy',
  category: '',
  relatedDiagnosisId: '',
  careTeamMemberId: '',
  startDate: '',
  targetDate: '',
  measurementType: 'percentage',
  baselineValue: '',
  targetValue: '',
  isIepGoal: false,
  iepArea: '',
  reviewFrequency: '',
  priority: 'medium',
  notes: '',
};

// ============================================================================
// Main Component
// ============================================================================

export function GoalForm({
  childId,
  familyId,
  goalId,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: GoalFormProps) {
  const [formData, setFormData] = useState<GoalFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch existing goal if editing
  const { data: existingGoal, isLoading: isLoadingGoal } = useGoal(goalId || null);

  // Fetch care team for assignment
  const { data: careTeamData } = useCareTeam(familyId);

  // Populate form when editing
  useEffect(() => {
    if (existingGoal) {
      setFormData({
        title: existingGoal.title,
        description: existingGoal.description || '',
        goalType: existingGoal.goalType,
        category: existingGoal.category || '',
        relatedDiagnosisId: existingGoal.relatedDiagnosisId || '',
        careTeamMemberId: existingGoal.careTeamMemberId || '',
        startDate: existingGoal.startDate || '',
        targetDate: existingGoal.targetDate || '',
        measurementType: existingGoal.measurementType,
        baselineValue: existingGoal.baselineValue || '',
        targetValue: existingGoal.targetValue || '',
        isIepGoal: existingGoal.isIepGoal,
        iepArea: existingGoal.iepArea || '',
        reviewFrequency: existingGoal.reviewFrequency || '',
        priority: existingGoal.priority,
        notes: existingGoal.notes || '',
      });
    }
  }, [existingGoal]);

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Auto-set isIepGoal when goalType is 'iep'
    if (name === 'goalType' && value === 'iep') {
      setFormData((prev) => ({ ...prev, isIepGoal: true }));
    }

    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.goalType) {
      newErrors.goalType = 'Goal type is required';
    }

    if (!formData.measurementType) {
      newErrors.measurementType = 'Measurement type is required';
    }

    if (formData.isIepGoal && !formData.iepArea) {
      newErrors.iepArea = 'IEP area is required for IEP goals';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const submitData: CreateGoalRequest = {
      title: formData.title,
      description: formData.description || undefined,
      goalType: formData.goalType,
      category: formData.category || undefined,
      relatedDiagnosisId: formData.relatedDiagnosisId || undefined,
      careTeamMemberId: formData.careTeamMemberId || undefined,
      startDate: formData.startDate || undefined,
      targetDate: formData.targetDate || undefined,
      measurementType: formData.measurementType,
      baselineValue: formData.baselineValue || undefined,
      targetValue: formData.targetValue || undefined,
      isIepGoal: formData.isIepGoal,
      iepArea: formData.iepArea || undefined,
      reviewFrequency: formData.reviewFrequency as ReviewFrequency || undefined,
      priority: formData.priority,
      notes: formData.notes || undefined,
    };

    await onSubmit(submitData);
  };

  if (isLoadingGoal) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-warm-gray/10">
        <h2 className="text-xl font-display font-semibold text-navy">
          {goalId ? 'Edit Goal' : 'Create New Goal'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-warm-gray hover:text-navy hover:bg-cream rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          Goal Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., Walk independently for 10 steps"
          className={`w-full px-4 py-3 bg-cream border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
            errors.title ? 'border-red-500' : 'border-transparent focus:border-sage'
          }`}
        />
        {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          placeholder="Describe the goal in detail..."
          className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors resize-none"
        />
      </div>

      {/* Goal Type & Category */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy mb-2">
            Goal Type <span className="text-red-500">*</span>
          </label>
          <select
            name="goalType"
            value={formData.goalType}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-cream border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
              errors.goalType ? 'border-red-500' : 'border-transparent focus:border-sage'
            }`}
          >
            {goalTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Category</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
          >
            <option value="">Select category...</option>
            {goalCategories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* IEP Toggle & Area */}
      <div className="p-4 bg-cream rounded-lg space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isIepGoal"
            name="isIepGoal"
            checked={formData.isIepGoal}
            onChange={handleChange}
            className="w-5 h-5 rounded border-warm-gray text-sage focus:ring-sage"
          />
          <label htmlFor="isIepGoal" className="text-sm font-medium text-navy">
            This is an IEP Goal
          </label>
        </div>
        {formData.isIepGoal && (
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              IEP Area <span className="text-red-500">*</span>
            </label>
            <select
              name="iepArea"
              value={formData.iepArea}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-white border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors ${
                errors.iepArea ? 'border-red-500' : 'border-transparent focus:border-sage'
              }`}
            >
              <option value="">Select IEP area...</option>
              {iepAreas.map((area) => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
            {errors.iepArea && <p className="mt-1 text-sm text-red-500">{errors.iepArea}</p>}
          </div>
        )}
      </div>

      {/* Measurement */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-navy-light uppercase tracking-wide">
          Measurement
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Measurement Type <span className="text-red-500">*</span>
            </label>
            <select
              name="measurementType"
              value={formData.measurementType}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
            >
              {measurementTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Baseline Value</label>
            <input
              type="text"
              name="baselineValue"
              value={formData.baselineValue}
              onChange={handleChange}
              placeholder="Starting point"
              className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy mb-2">Target Value</label>
            <input
              type="text"
              name="targetValue"
              value={formData.targetValue}
              onChange={handleChange}
              placeholder="Goal to achieve"
              className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Start Date</label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Target Date</label>
          <input
            type="date"
            name="targetDate"
            value={formData.targetDate}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
          />
        </div>
      </div>

      {/* Priority & Review Frequency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Priority</label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
          >
            {priorityOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Review Frequency</label>
          <select
            name="reviewFrequency"
            value={formData.reviewFrequency}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
          >
            <option value="">No scheduled review</option>
            {reviewFrequencyOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Assigned Provider */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Assigned Provider</label>
        <select
          name="careTeamMemberId"
          value={formData.careTeamMemberId}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors"
        >
          <option value="">Select provider...</option>
          {careTeamData?.items?.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name} {member.title ? `- ${member.title}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Additional notes or context..."
          className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:ring-0 focus:border-sage transition-colors resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-warm-gray/10">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-navy hover:bg-cream rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {goalId ? 'Save Changes' : 'Create Goal'}
        </button>
      </div>
    </form>
  );
}

export default GoalForm;

